import { classNames } from '@framework/utils';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePopupRef } from '@framework/hooks';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { useCallback, forwardRef, useImperativeHandle } from 'react';
import { type UserId } from 'balus-common';
import { type ReactionType } from '@view-model/models/sticky/NodeReaction';
import { reactionIcons, reactionTypeList } from '@view-model/models/sticky/NodeReaction/domain';
import { useCurrentUserPublicProfile } from '@user/PublicProfile/useUserPublicProfile';

type Props = {
  onReactionSelected(reactionName: ReactionType, userId: UserId, userName: string): void;
  onOpenSubMenu(menuType: string): void;
};

export const ReactionMenuButton = forwardRef<
  {
    closePopup: () => void;
  },
  Props
>(({ onReactionSelected, onOpenSubMenu }, ref) => {
  const { t } = useTranslation();
  const { isOpen, popupRef, handleToggle, handleClose } = usePopupRef<HTMLDivElement>(false);

  // 親コンポーネントから呼び出せるメソッドを定義
  useImperativeHandle(ref, () => ({
    closePopup: () => handleClose(),
  }));

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      onOpenSubMenu('reaction');
      handleToggle();
    },
    [handleToggle, onOpenSubMenu]
  );

  const profile = useCurrentUserPublicProfile();
  const handleReactionSelected = (reactionName: ReactionType) => () => {
    if (!profile) return;
    onReactionSelected(reactionName, profile.id, profile.name);
  };

  return (
    <div className="relative hover:bg-gray-200">
      <button
        // data-right-click-menu は親 item をクリックしても閉じないようにするためのマーカー
        data-right-click-menu
        className={classNames('flex w-full cursor-pointer px-8 py-4')}
        onClick={handleClick}
      >
        <div className="inline-block pl-4 pr-6">{t('canvas.menu.node.reaction')}</div>
        <FontAwesomeIcon icon={faAngleRight} className="ml-auto" />
      </button>
      <div
        className={classNames(
          'absolute -right-32 top-0 z-50 w-32 rounded-lg bg-white py-4 shadow-2xl',
          isOpen ? 'block' : 'hidden'
        )}
        ref={popupRef}
      >
        {reactionTypeList.map((reactionType) => {
          return (
            <ReactionMenuItem
              key={reactionType}
              reaction={reactionIcons[reactionType]}
              onClick={handleReactionSelected(reactionType)}
            />
          );
        })}
      </div>
    </div>
  );
});

ReactionMenuButton.displayName = 'ReactionMenuButton';

type ReactionMenuItemProps = {
  reaction: string;
  onClick(): void;
};

const ReactionMenuItem: React.FC<ReactionMenuItemProps> = ({ onClick, reaction }: ReactionMenuItemProps) => {
  return (
    <div className="flex w-full cursor-pointer py-6 pl-8 pr-12 hover:bg-gray-200" onClick={onClick}>
      <span className="flex size-full items-center justify-center text-5xl font-bold">{reaction}</span>
    </div>
  );
};
